<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { NIcon, type UploadCustomRequestOptions, type UploadFileInfo } from 'naive-ui';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaProgressBar from '../../base/GaiaProgressBar.vue';
import GaiaSegmented from '../../base/GaiaSegmented.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaBanner from '../../composite/GaiaBanner.vue';
import GaiaSubPanel from '../../composite/GaiaSubPanel.vue';
import GaiaVaultFolderPicker from './GaiaVaultFolderPicker.vue';
import type { VaultFolderPickerLocation } from './vaultFolderPicker';
import type { VaultUploadPanelApi } from './vaultUploadPanel';
import { IconClose, IconUpload } from '../../icons';
import { gaiaNeutral, gaiaRiskTint } from '../../themes/tokens';
import {
  runWithConcurrency,
  useVaultUpload,
  type UploadedVaultRevision,
} from '@/composables/useVaultUpload';
import { useVaultStore } from '@/stores/vaultStore';
import { formatFileSize, getFileExtension } from '@/utils/signatureFileValidation';
import { logger } from '@/utils/logger';
import { filterHiddenFiles, relativeDir, type DroppedFile } from '@/utils/vaultFileDrop';

/**
 * The body of every "upload into the vault" surface: where the files go, the
 * drop zone, and the files with their names. The host owns the frame and the
 * submit button and calls `upload()` — the vault's upload dialog, the playbook
 * and review dialogs, the file selector's Upload tab all sit on this one.
 *
 * A name clash never turns an upload into a version of the existing file: it
 * always creates a new one, numbered ("NDA (2).pdf", the default) or under the
 * same name, chosen per file before anything is sent.
 */
const props = withDefaults(
  defineProps<{
    /** Off resets the panel (a dialog closing); on loads the picker and folder names. */
    active?: boolean;
    /** The fixed target (the folder the vault shows); ignored with `selectableDestination`. */
    directoryUid?: string | null;
    /** Its name, stated in the "Uploading to" row. */
    directoryLabel?: string;
    /** Let the user pick the folder — for uploads that have none of their own. */
    selectableDestination?: boolean;
    /** The folder a selectable destination can't climb above (e.g. a data room). */
    scopeRootUid?: string;
    /** That folder's name, the picker's first crumb. */
    scopeRootLabel?: string;
    /** Files chosen elsewhere (a drop, the open Word document); replace the list when they change. */
    initialFiles?: File[];
    accept?: string;
    max?: number;
    /** Unpack an uploaded archive in place — off for flows that hand the file on as-is. */
    unpackArchives?: boolean;
    /** Pick a whole folder and recreate its structure (the vault's "Upload folder"). */
    folderUpload?: boolean;
    /** Let each file's name be edited (the open Word document, whose name is a guess). */
    renamable?: boolean;
    /** Hide the drop zone when the files are given (the open Word document). */
    hideDropzone?: boolean;
    /** The drop zone's second line. */
    hint?: string;
    /** Refused on pick, with the limit in the message. */
    maxSizeB?: number;
    /** Freezes the panel (a form submitting, or done with it). */
    disabled?: boolean;
  }>(),
  {
    active: true,
    directoryUid: null,
    directoryLabel: 'Home',
    scopeRootUid: undefined,
    scopeRootLabel: undefined,
    initialFiles: () => [],
    accept: undefined,
    max: undefined,
    unpackArchives: true,
    hint: 'Any file type',
    maxSizeB: undefined,
  }
);

const emit = defineEmits<{
  /** How many files are listed — hosts gate their submit and estimate cost on it. */
  'update:fileCount': [count: number];
  /** Whether `upload()` would run now: files listed, a folder picked, names checked. */
  'update:ready': [ready: boolean];
}>();

const vaultStore = useVaultStore();
const { uploadFile, uploadTree } = useVaultUpload();

type ClashChoice = 'numbered' | 'same';

const CLASH_CHOICE_OPTIONS = [
  { label: 'Add a number', value: 'numbered' },
  { label: 'Keep name', value: 'same' },
];

interface PlannedName {
  clash: boolean;
  /** The name the file is uploaded under, after the row's choice. */
  uploadName: string;
}

const uploadFiles = ref<UploadFileInfo[]>([]);
const uploading = ref(false);
const errors = ref<string[]>([]);
/** The selectable destination's current pick; null until the picker has loaded. */
const pickedLocation = ref<VaultFolderPickerLocation | null>(null);
const pickerOpen = ref(false);
/** Names typed over a row's own (`renamable`). */
const editedNames = ref<Record<string, string>>({});
/** Per row; a clashing row without an entry is numbered. */
const clashChoices = ref<Record<string, ClashChoice>>({});
/** Upload progress per row, 0–100. */
const rowProgress = ref<Record<string, number>>({});
const folderProgress = ref<{ done: number; total: number; creatingFolders: boolean } | null>(null);

/** Names of the files already in the target folder; null until loaded. */
const existingNames = ref<Set<string> | null>(null);
/** The folder `existingNames` belongs to — reloaded when the target changes. */
let existingNamesFor: string | undefined | null = null;
let existingNamesRequest = 0;

/** Where the files go; undefined is the vault root. */
const targetDirectoryUid = computed<string | undefined>(() =>
  props.selectableDestination
    ? pickedLocation.value?.uid
    : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      props.directoryUid || undefined
);
const targetLabel = computed<string>(() =>
  props.selectableDestination ? (pickedLocation.value?.label ?? '…') : props.directoryLabel
);

const locked = computed<boolean>(() => uploading.value || props.disabled);

const ready = computed<boolean>(() => {
  if (uploadFiles.value.length === 0 || locked.value) return false;
  if (props.selectableDestination && !pickedLocation.value) return false;
  if (props.renamable && rows.value.some(row => !row.name)) return false;
  // Names are only known once the folder's names are in.
  return props.folderUpload || existingNames.value !== null;
});

const byName = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** The list is always shown A→Z by file name, however the files arrived. */
function setFileList(fileList: UploadFileInfo[]): void {
  const refused: string[] = [];
  const accepted = fileList.filter(info => {
    const reason = info.file ? refusal(info.file) : null;
    if (reason) refused.push(`${info.name}: ${reason}`);
    return !reason;
  });
  errors.value = refused;
  uploadFiles.value = accepted.sort((a, b) => byName.compare(a.name, b.name));
}

const acceptedExtensions = computed<string[]>(
  () =>
    props.accept
      ?.split(',')
      .map(entry => entry.trim().replace(/^\./, '').toLowerCase())
      .filter(Boolean) ?? []
);

/**
 * What the upload would be refused for, said on pick. `accept` only filters
 * the browser's file dialog — a drop brings any file — so it is checked here too.
 */
function refusal(file: File): string | null {
  if (props.folderUpload) return null;
  if (acceptedExtensions.value.length > 0) {
    const extension = getFileExtension(file.name);
    if (!extension || !acceptedExtensions.value.includes(extension)) {
      return `only ${acceptedExtensions.value.map(entry => entry.toUpperCase()).join(', ')} files can be added here.`;
    }
  }
  if (props.maxSizeB !== undefined && file.size > props.maxSizeB) {
    return `larger than the maximum of ${formatFileSize(props.maxSizeB)}.`;
  }
  return null;
}

function reset(): void {
  existingNamesRequest++;
  existingNames.value = null;
  existingNamesFor = null;
  uploadFiles.value = [];
  editedNames.value = {};
  clashChoices.value = {};
  rowProgress.value = {};
  folderProgress.value = null;
  errors.value = [];
  uploading.value = false;
  pickerOpen.value = false;
}

watch(
  [(): boolean => props.active, (): File[] => props.initialFiles],
  ([active, files]) => {
    if (!active) {
      reset();
      return;
    }
    if (files.length === 0) return;
    editedNames.value = {};
    setFileList(
      files.map(file => ({ id: crypto.randomUUID(), name: file.name, status: 'pending', file }))
    );
  },
  { immediate: true }
);

// Clashes show as soon as there is something to upload, and are re-checked
// whenever the target folder changes. A folder upload builds fresh folders, so
// nothing in it can clash.
watch(
  [
    (): boolean => props.active,
    targetDirectoryUid,
    (): boolean => props.selectableDestination && pickedLocation.value === null,
    (): boolean => uploadFiles.value.length > 0,
  ],
  ([active, target, pickPending, filesPresent]) => {
    if (!active || props.folderUpload || pickPending || !filesPresent) return;
    if (existingNames.value !== null && existingNamesFor === target) return;
    void loadExistingNames(target);
  },
  { immediate: true }
);

async function loadExistingNames(target: string | undefined): Promise<void> {
  const request = ++existingNamesRequest;
  existingNames.value = null;
  existingNamesFor = target;
  try {
    // The whole folder: a clashing file may sit on any page of the listing.
    // No target means the vault root, for the listing as for the upload.
    const items = await vaultStore.getWholeDirectoryItems(target);
    if (request !== existingNamesRequest) return;
    existingNames.value = new Set(
      items.filter(item => item.type === 'file').map(item => item.name)
    );
  } catch (error) {
    if (request !== existingNamesRequest) return;
    logger.error('Failed to list the target folder:', error);
    errors.value.push('Couldn’t check this folder for files with the same name.');
    existingNamesFor = null;
  }
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot) : '';
}

/** A typed name keeps the file's extension, so "Offer" for a .docx becomes "Offer.docx". */
function withExtension(typed: string, original: string): string {
  const trimmed = typed.trim();
  if (!trimmed) return '';
  const extension = extensionOf(original);
  return extension && !trimmed.toLowerCase().endsWith(extension.toLowerCase())
    ? `${trimmed}${extension}`
    : trimmed;
}

/** The rows in list order, under the name each is listed as. */
const rows = computed<{ id: string; name: string; info: UploadFileInfo }[]>(() =>
  uploadFiles.value.map(info => {
    return {
      id: info.id,
      name:
        info.id in editedNames.value
          ? withExtension(editedNames.value[info.id], info.name)
          : info.name,
      info,
    };
  })
);

/**
 * What each row is uploaded as. A row clashes with the folder and with every
 * earlier row; a numbered one takes the first free "name (n).ext", so a batch
 * never hands two files the same new name.
 */
const plannedNames = computed<Map<string, PlannedName>>(() => {
  const plan = new Map<string, PlannedName>();
  if (!existingNames.value) return plan;
  const taken = new Set(existingNames.value);
  for (const row of rows.value) {
    if (!row.name) continue;
    const clash = taken.has(row.name);
    const choice = clashChoices.value[row.id] ?? 'numbered';
    const uploadName = clash && choice === 'numbered' ? nextFreeName(row.name, taken) : row.name;
    taken.add(uploadName);
    plan.set(row.id, { clash, uploadName });
  }
  return plan;
});

// After `rows`: an immediate watch reads `ready`, which reads `rows`.
watch(
  ready,
  value => {
    emit('update:ready', value);
  },
  { immediate: true }
);
watch(
  () => uploadFiles.value.length,
  count => {
    emit('update:fileCount', count);
  },
  { immediate: true }
);

function nextFreeName(name: string, taken: Set<string>): string {
  const extension = extensionOf(name);
  const stem = name.slice(0, name.length - extension.length);
  let n = 2;
  while (taken.has(`${stem} (${n})${extension}`)) n++;
  return `${stem} (${n})${extension}`;
}

/** The file under the name it is uploaded as. */
function withPlannedName(id: string, file: File): File {
  const name = plannedNames.value.get(id)?.uploadName ?? file.name;
  if (name === file.name) return file;
  return new File([file], name, { type: file.type, lastModified: file.lastModified });
}

function setClashChoice(id: string, choice: string): void {
  clashChoices.value = { ...clashChoices.value, [id]: choice as ClashChoice };
}

function setEditedName(id: string, name: string): void {
  editedNames.value = { ...editedNames.value, [id]: name };
}

function removeRow(id: string): void {
  uploadFiles.value = uploadFiles.value.filter(fi => fi.id !== id);
}

function handleCustomUpload({ onFinish }: UploadCustomRequestOptions): void {
  // Nothing is sent on pick; `upload()` sends the list.
  onFinish();
}

/** The picked folder's files with their paths, hidden ones left out. */
const folderFiles = computed<DroppedFile[]>(() =>
  filterHiddenFiles(
    uploadFiles.value.map(fileInfo => {
      const file = fileInfo.file as File;
      return { file, dir: relativeDir(file) };
    })
  )
);

const folderName = computed<string>(() => {
  const relativePath = uploadFiles.value[0]?.file?.webkitRelativePath ?? '';
  return relativePath.split('/')[0] || 'Folder';
});

/**
 * Upload the listed files into the target. Resolves with what landed, in list
 * order (empty for a folder upload, which lands as a tree), or null when
 * something failed — the panel lists the errors and keeps the files.
 */
async function upload(): Promise<UploadedVaultRevision[] | null> {
  if (!ready.value) return null;
  errors.value = [];
  uploading.value = true;
  try {
    if (props.folderUpload) {
      const files = folderFiles.value;
      folderProgress.value = { done: 0, total: files.length, creatingFolders: true };
      const result = await uploadTree(files, targetDirectoryUid.value, () => {
        if (!folderProgress.value) return;
        folderProgress.value.creatingFolders = false;
        folderProgress.value.done++;
      });
      errors.value.push(...result.failed);
      return result.failed.length > 0 ? null : [];
    }

    const landed = new Map<string, UploadedVaultRevision>();
    await runWithConcurrency(uploadFiles.value, async fi => {
      const file = withPlannedName(fi.id, fi.file as File);
      try {
        landed.set(
          fi.id,
          await uploadFile(
            file,
            { directoryUid: targetDirectoryUid.value, unpackArchives: props.unpackArchives },
            percent => {
              rowProgress.value = { ...rowProgress.value, [fi.id]: percent };
            }
          )
        );
      } catch (error) {
        const reason = error instanceof Error ? error.message : 'Unknown upload error';
        errors.value.push(`Couldn’t upload ${file.name}: ${reason}`);
      }
    });
    if (errors.value.length > 0) {
      // What did land is out of the list, so trying again sends only the rest.
      uploadFiles.value = uploadFiles.value.filter(fi => !landed.has(fi.id));
      return null;
    }
    return uploadFiles.value.flatMap(fi => landed.get(fi.id) ?? []);
  } finally {
    uploading.value = false;
  }
}

const api: VaultUploadPanelApi = {
  upload,
  files: () => uploadFiles.value.map(fi => fi.file as File),
  reset,
};
defineExpose(api);

const folderPercentage = computed<number>(() =>
  folderProgress.value && folderProgress.value.total > 0
    ? Math.round((folderProgress.value.done / folderProgress.value.total) * 100)
    : 0
);

// Bound from the template (an icon colour), so it stays a plain const.
const subtleColor = gaiaNeutral.textSubtle;

/**
 * Inline custom properties rather than `v-bind()`: hosts mount this inside a
 * GaiaModal, whose Teleport root leaves `v-bind()` vars with no element.
 */
const styleVars = {
  '--vup-subtle-color': gaiaNeutral.textSubtle,
  '--vup-target-surface': gaiaNeutral.surfaceTint,
  '--vup-row-border': gaiaNeutral.borderSoft,
  '--vup-clash-surface': gaiaRiskTint.warningSurface,
  '--vup-clash-border': gaiaRiskTint.warningBorder,
};
</script>

<template>
  <n-flex vertical :size="14" :wrap="false" :style="styleVars">
    <!-- Where these files land. In the vault it is decided by where you were,
         so it is stated; elsewhere it can be changed in the picker below. -->
    <n-flex align="center" :size="8" :wrap="false" class="target">
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        :style="{ flex: 'none' }"
      >
        <path d="M2 4.5h4.5l1.5 2H14v6H2z" />
      </svg>
      <gaia-text size="control" tone="tertiary">Uploading to</gaia-text>
      <gaia-text truncate size="control" weight="semibold" tone="ink">
        {{ targetLabel }}
      </gaia-text>
      <gaia-button
        v-if="props.selectableDestination"
        size="tiny"
        quaternary
        :disabled="locked"
        :style="{ marginLeft: 'auto' }"
        @click="pickerOpen = !pickerOpen"
      >
        {{ pickerOpen ? 'Done' : 'Change' }}
      </gaia-button>
    </n-flex>

    <!-- Mounted for as long as the panel is active, only shown on demand: the
         pick lives in the picker, and remounting would reset it to the top. -->
    <gaia-vault-folder-picker
      v-if="props.selectableDestination"
      v-show="pickerOpen"
      :active="props.active"
      :root-uid="props.scopeRootUid"
      :root-label="props.scopeRootLabel"
      :disabled="locked"
      allow-create
      empty-hint="Upload here, or open one of its subfolders."
      @update:location="pickedLocation = $event"
    />

    <n-upload
      v-if="!props.hideDropzone"
      :multiple="!props.folderUpload"
      :directory="props.folderUpload"
      :directory-dnd="false"
      :default-upload="false"
      :show-file-list="false"
      :file-list="uploadFiles"
      :accept="props.accept"
      :max="props.max"
      :disabled="locked"
      :custom-request="handleCustomUpload"
      @update:file-list="setFileList"
    >
      <n-upload-dragger class="dropzone">
        <n-flex vertical align="center" :size="8">
          <n-icon :component="IconUpload" :size="24" :color="subtleColor" />
          <gaia-text size="row" weight="medium" tone="ink">
            {{ props.folderUpload ? 'Click to choose a folder' : 'Click or drag files here' }}
          </gaia-text>
          <gaia-text size="control" tone="faint">
            {{ props.folderUpload ? 'The folder structure is recreated in the vault' : props.hint }}
          </gaia-text>
        </n-flex>
      </n-upload-dragger>
    </n-upload>

    <!-- Our own list rather than naive's: a row that clashes with a file
         already in the folder carries its choice of name inline. -->
    <n-flex
      v-if="!props.folderUpload && rows.length > 0"
      vertical
      :size="6"
      :wrap="false"
      class="file-rows"
    >
      <n-flex
        v-for="row in rows"
        :key="row.id"
        vertical
        :size="6"
        :wrap="false"
        class="file-row"
        :class="{ clash: plannedNames.get(row.id)?.clash }"
      >
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-input
            v-if="props.renamable"
            size="small"
            :value="editedNames[row.id] ?? row.info.name"
            :disabled="locked"
            placeholder="File name"
            :style="{ flex: 1, minWidth: 0 }"
            @update:value="setEditedName(row.id, $event)"
          />
          <gaia-text v-else truncate size="row" tone="ink" :style="{ flex: 1, minWidth: 0 }">
            {{ row.name }}
          </gaia-text>
          <gaia-text v-if="uploading" size="meta" tone="subtle">
            {{ rowProgress[row.id] ?? 0 }}%
          </gaia-text>
          <gaia-text v-else-if="row.info.file" size="meta" tone="subtle">
            {{ formatFileSize(row.info.file.size) }}
          </gaia-text>
          <gaia-button
            v-if="!props.hideDropzone"
            size="tiny"
            quaternary
            circle
            :disabled="locked"
            :aria-label="`Remove ${row.name}`"
            @click="removeRow(row.id)"
          >
            <template #icon><n-icon :component="IconClose" /></template>
          </gaia-button>
        </n-flex>
        <n-flex
          v-if="plannedNames.get(row.id)?.clash"
          align="center"
          justify="space-between"
          :size="8"
          :wrap="false"
        >
          <gaia-text truncate size="meta" tone="subtle">
            Already in this folder · saved as
            <gaia-text size="meta" weight="semibold" tone="ink">
              {{ plannedNames.get(row.id)?.uploadName }}
            </gaia-text>
          </gaia-text>
          <gaia-segmented
            :value="clashChoices[row.id] ?? 'numbered'"
            :options="CLASH_CHOICE_OPTIONS"
            :disabled="locked"
            @update:value="setClashChoice(row.id, $event)"
          />
        </n-flex>
      </n-flex>
    </n-flex>

    <gaia-sub-panel v-if="props.folderUpload && uploadFiles.length > 0" label="Selected folder">
      <n-flex vertical :size="2">
        <gaia-text size="row" weight="semibold" tone="ink">{{ folderName }}</gaia-text>
        <gaia-text size="meta" tone="subtle">
          {{ folderFiles.length }} {{ folderFiles.length === 1 ? 'file' : 'files' }} will be
          uploaded
        </gaia-text>
      </n-flex>
    </gaia-sub-panel>

    <n-flex v-if="folderProgress" vertical :size="7">
      <gaia-progress-bar :percentage="folderPercentage" />
      <gaia-text size="control" tone="tertiary">
        {{
          folderProgress.creatingFolders
            ? 'Creating the folder structure…'
            : `Uploaded ${folderProgress.done} of ${folderProgress.total} files`
        }}
      </gaia-text>
    </n-flex>

    <gaia-banner v-for="(error, index) in errors" :key="index" type="error">
      {{ error }}
    </gaia-banner>
  </n-flex>
</template>

<style scoped>
.target {
  padding: 9px 12px;
  border-radius: 9px;
  background: var(--vup-target-surface);
  color: var(--vup-subtle-color);
}

/* naive's dragger supplies the dashed frame; these are the mock's metrics. */
.dropzone {
  padding: 32px 20px;
  border-radius: 11px;
}

/* Left uncapped, a long selection pushes the host dialog — header and footer
   included — past the viewport. */
.file-rows {
  max-height: 240px;
  overflow-y: auto;
}

.file-row {
  padding: 6px 6px 6px 10px;
  border: 1px solid var(--vup-row-border);
  border-radius: 8px;
}

/* A file already in the target folder: the row stays amber while it shows
   which name it will be saved under. */
.file-row.clash {
  background: var(--vup-clash-surface);
  border-color: var(--vup-clash-border);
}
</style>
